import React, { useState, useEffect } from 'react';
import { Toastify, Button, SpinnerLoader, Input, Label, Checkbox, Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SingleSelect } from 'lift-ui';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle } from 'lucide-react';
import { CDValue, CDType } from '../TaxonomyMerge';
import apiClient from '@lift-admin/utils/apiClient';
import { actionLog } from "@/App";
import {
  buildSummary,
  formatIdWithName,
} from "@lift-admin/utils/logChangeFormatter";

interface CreateValueDialogProps {
  openMergeDialog: boolean;
  setOpenMergeDialog: (open: boolean) => void;
  selectedVals: number[];
  setSelectedVals: (vals: number[]) => void;
  callTaxonomyMerge: boolean;
  setCallTaxonomyMerge: (call: boolean) => void;
  nonMergedValues: CDValue[];
  cdTypes: CDType[];
}

interface ValidationError {
  message: string;
  details?: string[];
}

const TaxonomyMergeValueCreateDialog: React.FC<CreateValueDialogProps> = ({
  openMergeDialog,
  setOpenMergeDialog,
  selectedVals,
  setSelectedVals,
  setCallTaxonomyMerge,
  callTaxonomyMerge,
  nonMergedValues,
  cdTypes,
}) => {
  const [loader, setLoader] = useState(false);
  const [valueName, setValueName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedType, setSelectedType] = useState<number | ''>('');
  const [isMandatory, setIsMandatory] = useState(false);
  const [validationError, setValidationError] = useState<ValidationError | null>(null);

  useEffect(() => {
    if (selectedVals.length > 0) {
      const selectedValue = nonMergedValues.find((val) => val.id === selectedVals[0]);
      if (selectedValue) {
        setSelectedType(selectedValue.custom_domain_type_id);
      }
    } else {
      setSelectedType('');
    }
  }, [selectedVals, nonMergedValues]);

  const parseErrorResponse = (error: any): ValidationError => {
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (Array.isArray(detail)) {
        return {
          message: 'Validation failed',
          details: detail.map((err) =>
            typeof err === 'object' ? err.msg || JSON.stringify(err) : String(err)
          ),
        };
      }
      return {
        message:
          typeof detail === 'object' ? detail.msg || JSON.stringify(detail) : String(detail),
      };
    }
    return {
      message: error.message || 'An unexpected error occurred',
    };
  };

  const getSelectedBrandId = (): number | null => {
    if (!selectedVals.length) return null;
    const selectedValue = nonMergedValues.find((val) => val.id === selectedVals[0]);
    return selectedValue ? selectedValue.brand_id : null;
  };

  const validateInputs = (): boolean => {
    if (!selectedVals.length) {
      setValidationError({ message: 'Please select at least one value to merge.' });
      return false;
    }
    if (!valueName.trim()) {
      setValidationError({ message: 'Value name is required.' });
      return false;
    }
    if (!selectedType) {
      setValidationError({ message: 'Please select a type.' });
      return false;
    }
    const brandId = getSelectedBrandId();
    if (!brandId) {
      setValidationError({ message: 'Unable to determine brand ID.' });
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleMergeConfirm = async () => {
    if (!validateInputs()) return;

    setLoader(true);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const brandId = getSelectedBrandId();

    const payload = {
      value: valueName.trim(),
      display_name: description.trim() || undefined,
      custom_domain_type_id: selectedType,
      brand_id: brandId!,
      merged_column_ids: selectedVals,
      created_by: user.id,
    };

    try {
      const response = await apiClient.post(`txnmy/api/v1/cdvalue/add`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data.message === 'success') {
        const sourceNames = selectedVals.map((id) => {
          const val = nonMergedValues.find((v) => v.id === id);
          return val?.value || val?.display_name || `ID ${id}`;
        });
        const typeName =
          cdTypes.find((t) => String(t.id) === String(selectedType))?.name ||
          String(selectedType);
        const brandName =
          nonMergedValues.find((v) => v.id === selectedVals[0])?.brand_name ||
          "";
        const logDesc = {
          message: buildSummary(
            "Merged taxonomy value",
            valueName.trim(),
            "created"
          ),
          status: response.status,
          method: response.config?.method || "POST",
          url: response.config?.url || "/txnmy/api/v1/cdvalue/add",
          category: "taxonomy",
          action: "create_merged_cdvalue",
          details: [
            { label: "Value", value: valueName.trim() || "Not Set" },
            {
              label: "Display Name",
              value: description.trim() || "Not Set",
            },
            { label: "Type", value: typeName },
            {
              label: "Brand ID",
              value: formatIdWithName(brandId, brandName),
            },
            {
              label: "Merged From",
              value: sourceNames.join(", ") || "None",
            },
          ],
          changes: [
            {
              label: "Merged Values",
              added: sourceNames,
              removed: [],
            },
          ],
        };
        await actionLog?.actionLogTrack(JSON.stringify(logDesc), false);

        Toastify('success', 'Merged value created successfully.');
        handleDialogClose(true);
        setCallTaxonomyMerge(!callTaxonomyMerge);
      } else {
        throw new Error('Unexpected response format.');
      }
    } catch (error: any) {
      console.error('Error creating merge:', error);
      const parsedError = parseErrorResponse(error);
      const rawDetail = error.response?.data?.detail;
      const detailStr = typeof rawDetail === 'string' ? rawDetail : JSON.stringify(rawDetail ?? '');
      const isDuplicate = /duplicate|unique constraint|unq_custom_domain_values/i.test(detailStr);
      const toastMessage = isDuplicate ? 'This value already exists for this type and brand.' : parsedError.message;
      Toastify('error', toastMessage);

      const errorLogDesc = {
        message: `Failed to create merged taxonomy value "${valueName.trim() || "N/A"}": ${parsedError.message}`,
        status: error.response?.status || 500,
        method: "POST",
        url: "/txnmy/api/v1/cdvalue/add",
        category: "taxonomy",
        action: "create_merged_cdvalue",
        error: parsedError.message,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorLogDesc), true);
    } finally {
      setLoader(false);
    }
  };

  const handleDialogClose = (clearSelection = false) => {
    setOpenMergeDialog(false);
    if (clearSelection) {
      setSelectedVals([]);
    }
    setValueName('');
    setDescription('');
    setSelectedType('');
    setIsMandatory(false);
    setValidationError(null);
  };

  return (
    <Sheet open={openMergeDialog} onOpenChange={(open) => !open && handleDialogClose()}>
      <SheetContent side="right" className="sm:max-w-md flex flex-col h-full" onInteractOutside={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <SheetHeader>
          <SheetTitle>Create New Merged Value</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto min-h-0 py-4">
          <div className="space-y-4">
            {validationError && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-sm">{validationError.message}</span>
                  {validationError.details && validationError.details.length > 0 && (
                    <ul className="mt-1 mb-0 pl-4 text-sm list-disc">
                      {validationError.details.map((detail, index) => (
                        <li key={index}>{detail}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="value-name">Value Name *</Label>
              <Input
                id="value-name"
                placeholder="Enter value name"
                value={valueName}
                onChange={(e) => setValueName(e.target.value)}
                className={!valueName.trim() && validationError ? 'border-destructive' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type-select">Type *</Label>
              <SingleSelect
                options={cdTypes
                  .filter((type) => type.id === selectedType)
                  .map((type) => ({ label: type.name, value: String(type.id) }))}
                defaultValue={selectedType === '' ? '' : String(selectedType)}
                onChange={(v) => setSelectedType(v === '' ? '' : Number(v))}
                disabled
                placeholder={
                  selectedType
                    ? 'Type auto-selected based on values'
                    : 'Select values first'
                }
                containerClassName={`w-full ${!selectedType && validationError ? 'border-destructive' : ''}`}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="display-name">Display Name *</Label>
              <Textarea
                id="display-name"
                placeholder="Enter display name"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="resize-none"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="value-is-mandatory"
                checked={isMandatory}
                onCheckedChange={(checked) => setIsMandatory(checked === true)}
              />
              <Label htmlFor="value-is-mandatory" className="font-normal cursor-pointer">
                Is Mandatory
              </Label>
            </div>
          </div>
        </div>
        <SheetFooter className="gap-2 border-t pt-4 mt-auto flex-shrink-0">
          <Button variant="outline" onClick={() => handleDialogClose()} disabled={loader}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleMergeConfirm}
            disabled={loader || !valueName || !selectedType}
            className="bg-primary hover:bg-primary/90"
          >
            {loader ? <SpinnerLoader className="h-4 w-4 mr-2" /> : null}
            {loader ? 'Creating...' : 'Create Merged Value'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default TaxonomyMergeValueCreateDialog;
